import type { HealthCheckStatus } from '@gonfalon/health-check/types';

import { isLDHosted } from '../isLDHosted';
import { MetricKind } from '../MetricKind';

import type { HealthCheckItem, MetricDefinition, MetricHealthResponse } from './constants';

export const isWithinLast90Days = (ts: number): boolean => {
  const now = Date.now();
  const ninetyDays = 90 * 24 * 60 * 60 * 1000;
  return ts >= now - ninetyDays;
};

const getEventKind = (metric: MetricDefinition) => {
  if (metric.kind === 'click') {
    return MetricKind.CLICK;
  } else if (metric.kind === 'pageview') {
    return MetricKind.PAGEVIEW;
  } else if (metric.kind === 'custom') {
    if (metric.isNumeric) {
      return MetricKind.NUMERIC;
    } else if (metric.unitAggregationType === 'sum') {
      return MetricKind.COUNT;
    } else if (metric.unitAggregationType === 'average') {
      return MetricKind.BINARY;
    }
  }
};

export const getHealthList = (
  eventKey: string,
  metric: MetricDefinition,
  isError: boolean,
  data: MetricHealthResponse,
): HealthCheckItem[] => {
  const eventKind = getEventKind(metric);
  const showExplicitWarning = metric.successCriteria === 'LowerThanBaseline' && eventKind === MetricKind.BINARY;
  // Case A: missing eventKey
  if (!eventKey) {
    return [
      {
        status: 'waiting',
        message: 'No event key selected',
      },
    ];
  }

  // Case B: missing randomization units
  if (!metric.randomizationUnits || metric.randomizationUnits?.length === 0) {
    return [
      {
        status: 'waiting',
        message: 'No randomization units selected',
      },
    ];
  }

  // Case C: API error or missing data
  if (isError || !data) {
    return [
      {
        status: 'error',
        message: 'Health check failed to load.',
      },
    ];
  }

  // Case D: the real health‐check components
  // Calculate statuses
  const eventsSeen = data.lastSeen && isWithinLast90Days(data.lastSeen);

  if (!eventsSeen) {
    return [
      {
        status: 'warning',
        message: 'No events received in the last 90 days',
      },
    ];
  }

  const evaluationPeriodDays = metric.dataSource && isLDHosted(metric.dataSource?.key) ? '14' : '7';
  const randomizationUnitsDetails: { status: HealthCheckStatus; message: string } = (() => {
    const overlappingRandomizationUnits = data.contextKinds ?? [];
    const randomizationUnits = metric.randomizationUnits ?? [];
    const someMatchingUnits = randomizationUnits.some((randomizationUnit) =>
      overlappingRandomizationUnits.includes(randomizationUnit),
    );
    const allMatchingUnits = randomizationUnits.every((randomizationUnit) =>
      overlappingRandomizationUnits.includes(randomizationUnit),
    );
    if (overlappingRandomizationUnits.length === 0) {
      return {
        status: 'warning',
        message: `No events available to evaluate selected randomization units in the last ${evaluationPeriodDays} days`,
      };
    }
    if (!someMatchingUnits) {
      return {
        status: showExplicitWarning ? 'warning' : 'fail',
        message: `Selected randomization units have not received events in the last ${evaluationPeriodDays} days`,
      };
    }
    if (allMatchingUnits) {
      return {
        status: 'pass',
        message: `All randomization units have events in the last ${evaluationPeriodDays} days`,
      };
    }
    return {
      status: 'warning',
      message: `Some randomization units have events in the last ${evaluationPeriodDays} days`,
    };
  })();

  const numericValuesDetails: { status: HealthCheckStatus; message: string } = (() => {
    if (!metric.isNumeric || !isLDHosted(metric.dataSource?.key)) {
      return {
        status: 'waiting',
        message: '',
      };
    }
    if (!data.sampleInstancesSeen) {
      return {
        status: 'warning',
        message: 'No events with numeric values received in the last 30 days',
      };
    }
    if (data.numericValuesFound && data.allValuesNumeric) {
      return {
        status: 'pass',
        message: 'All sample events included numeric values in the last 30 days',
      };
    }
    if (data.numericValuesFound) {
      return {
        status: 'warning',
        message: 'Not all sample events included numeric values in the last 30 days',
      };
    }
    return {
      status: showExplicitWarning ? 'warning' : 'fail',
      message: 'No sample events have included numeric values in the last 30 days',
    };
  })();

  return [
    {
      status: 'pass',
      message: 'Events received in the last 90 days',
    },
    {
      status: randomizationUnitsDetails.status,
      message: randomizationUnitsDetails.message,
    },
    {
      status: numericValuesDetails.status,
      message: numericValuesDetails.message,
    },
  ];
};
